<template>
  <div>
    <b-card class="filter-card mb-2">
      <div class="d-flex align-items-center justify-content-between flex-wrap mb-1">
        <div>
          <b-link class="d-inline-flex align-items-center text-muted mb-50"
            :to="{ name: 'client-group-members', query: { gid: groupId } }">
            <feather-icon icon="ArrowLeftIcon" size="14" class="mr-50" />
            <span>{{ $t('Back') }}</span>
          </b-link>
          <h4 class="mb-0">{{ $t('Add Members') }}</h4>
          <small class="text-muted">{{ $t('Select employees to add to the group') }}<span v-if="groupName"> — {{
            groupName }}</span></small>
        </div>
      </div>

      <div class="d-flex flex-column flex-md-row mb-1 radio-list">
        <label class="radio-option mb-1 mr-md-1" :class="{ active: searchType === 'MODE_SINGLE' }">
          <input type="radio" v-model="searchType" value="MODE_SINGLE" name="search-type">
          <span>{{ $t('Single Employee') }}</span>
        </label>
        <label class="radio-option mb-1 mr-md-1" :class="{ active: searchType === 'MODE_GROUP' }">
          <input type="radio" v-model="searchType" value="MODE_GROUP" name="search-type">
          <span>{{ $t('Employee by Group') }}</span>
        </label>
        <label class="radio-option mb-1 mr-md-1" :class="{ active: searchType === 'MODE_DEPARTMENT' }">
          <input type="radio" v-model="searchType" value="MODE_DEPARTMENT" name="search-type">
          <span>{{ $t('Employee by Department') }}</span>
        </label>
        <label class="radio-option mb-1 mr-md-1" :class="{ active: searchType === 'MODE_POSITION' }">
          <input type="radio" v-model="searchType" value="MODE_POSITION" name="search-type">
          <span>{{ $t('Employee by Position') }}</span>
        </label>
        <label class="radio-option mb-1" :class="{ active: searchType === 'MODE_ALL' }">
          <input type="radio" v-model="searchType" value="MODE_ALL" name="search-type">
          <span>{{ $t('All Employees') }}</span>
        </label>
      </div>

      <div v-if="searchType === 'MODE_SINGLE'" class="picker-wrap">
        <h6 class="text-muted">{{ $t('Select Employee') }}</h6>
        <member-picker @selectionChanged="onSingleMemberChanged" />

        <div v-if="selectedSingleMember" class="selected-single-preview mt-1 d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <b-avatar size="36" :src="selectedSingleMember.photo" :text="avatarText(selectedSingleMember.fullname)" class="mr-1" />
            <div>
              <div class="font-weight-bold">{{ selectedSingleMember.fullname }}</div>
              <small class="text-muted">{{ selectedSingleMember.username || selectedSingleMember.email }}</small>
            </div>
          </div>
          <b-button variant="link" size="sm" class="p-0" @click="clearSelection">{{ $t('Clear all') }}</b-button>
        </div>
      </div>
      <div v-if="searchType === 'MODE_GROUP'" class="picker-wrap">
        <h6 class="text-muted">{{ $t('Select Group') }}</h6>
        <group-picker :initialCategories="groupSubject" @selectionChanged="onGroupChanged" />
      </div>
      <div v-if="searchType === 'MODE_DEPARTMENT'" class="picker-wrap">
        <h6 class="text-muted">{{ $t('Select Department') }}</h6>
        <department-picker :initialCategories="departmentSubject" @selectionChanged="onDepartmentChanged" />
      </div>
      <div v-if="searchType === 'MODE_POSITION'" class="picker-wrap">
        <h6 class="text-muted">{{ $t('Select Position') }}</h6>
        <position-picker :initialCategories="positionSubject" @selectionChanged="onPositionChanged" />
      </div>
    </b-card>

    <b-card v-if="searchType !== 'MODE_SINGLE'" no-body class="mb-0">
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>{{ $t('Show') }}</label>
            <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
              :clearable="false" class="per-page-selector d-inline-block mx-50" />
            <label>{{ $t('entries') }}</label>
          </b-col>
          <b-col md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="searchQuery" class="d-inline-block" :placeholder="$t('Search...')" />
            </div>
          </b-col>
        </b-row>

        <b-row v-if="selectedCount || canSelectAllMatching" class="mt-1">
          <b-col cols="12" class="d-flex align-items-center justify-content-end flex-wrap">
            <small class="text-muted mr-1">
              {{ selectedCount }} {{ $t('selected') }}
            </small>
            <b-button v-if="canSelectAllMatching" variant="link" size="sm" class="p-0 mr-1"
              :disabled="isSelectingAllMatching" @click="selectAllMatching">
              <b-spinner v-if="isSelectingAllMatching" small class="mr-25" />
              {{ $t('Select all') }} ({{ totalUsers }})
            </b-button>
            <b-button v-if="selectedCount" variant="link" size="sm" class="p-0" @click="clearSelection">
              {{ $t('Clear all') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-table ref="refTable" class="position-relative" :items="tableItems" responsive :fields="tableColumns"
        primary-key="id" show-empty :empty-text="emptyText" :busy="isLoading">
        <template #head(selected)>
          <b-form-checkbox :checked="isAllSelectedOnPage" :disabled="!tableItems.length"
            @change="toggleSelectAllOnPage" />
        </template>

        <template #head()="data">
          <span v-if="data.column !== 'selected'" class="text">{{ $t(data.label).toUpperCase() }}</span>
        </template>

        <template #cell(selected)="data">
          <b-form-checkbox :checked="isRowSelected(data.item.id)" @change="toggleRow(data.item)" />
        </template>

        <template #cell(fullname)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar size="32" :text="avatarText(data.item.fullname)" />
            </template>
            <span class="font-weight-bold d-block text-nowrap">{{ data.item.fullname }}</span>
            <small class="text-muted">{{ data.item.username || data.item.email }}</small>
          </b-media>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>{{ $t('Loading') }}...</strong>
          </div>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">{{ $t('Showing') }} {{ dataMeta.from }} {{ $t('to') }} {{ dataMeta.to }}
              {{ $t('of') }} {{ dataMeta.of }} {{ $t('entries') }}</span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination v-model="currentPage" :total-rows="totalUsers" :per-page="perPage" first-number last-number
              class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item">
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <div class="add-participants-footer">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <small class="text-muted mb-1 mb-md-0">
          {{ selectedCount }} {{ $t('selected') }}
        </small>
        <div>
          <b-button variant="outline-secondary" class="mr-1"
            :to="{ name: 'client-group-members', query: { gid: groupId } }">
            {{ $t('Cancel') }}
          </b-button>
          <b-button variant="primary" :disabled="!selectedCount || submitting" @click="handleAddMembers">
            <b-spinner v-if="submitting" small class="mr-50" />
            {{ $t('Add') }} <span v-if="selectedCount">({{ selectedCount }})</span>
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BPagination, BFormCheckbox, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import router from '@/router'
import axios from '@axios'
import { avatarText } from '@core/utils/filter'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import MemberPicker from '@/views/members/component/MemberPicker.vue'
import GroupPicker from '@/views/clientgroups/components/GroupSelector.vue'
import DepartmentPicker from '@/views/department/components/DepartmentPicker.vue'
import PositionPicker from '@/views/position/component/PositionPicker.vue'

import clientGroupStoreModule from '../clientGroupStoreModule'
import memberStoreModule from '../../members/memberStoreModule'

const CLIENT_GROUPS_MODULE = 'client-groups'
const MEMBERS_MODULE = 'apps-members'

export default {
  components: {
    BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
    BPagination, BFormCheckbox, BSpinner,
    vSelect,
    MemberPicker,
    GroupPicker,
    DepartmentPicker,
    PositionPicker,
  },
  data() {
    return {
      avatarText,
      groupId: this.$route.query.gid,
      groupName: '',
      searchType: 'MODE_SINGLE',
      groupSubject: null,
      departmentSubject: null,
      positionSubject: null,
      groupSelection: null,
      departmentSelection: null,
      positionSelection: null,

      // server-paginated results (group / all employees)
      serverItems: [],
      serverTotal: 0,

      // client-paginated results (department / position)
      fullList: [],

      perPage: 10,
      perPageOptions: [10, 25, 50, 100],
      currentPage: 1,
      searchQuery: '',
      debouncedSearchQuery: '',
      searchDebounceTimer: null,
      isLoading: false,
      submitting: false,
      isSelectingAllMatching: false,
      allMatchingSelected: false,

      selectedMap: {},
    }
  },
  computed: {
    tableColumns() {
      if (this.searchType === 'MODE_ALL') {
        return [
          { key: 'selected', label: '', sortable: false, class: 'text-center', thStyle: { width: '48px' } },
          { key: 'membercode', label: 'Register No', sortable: false },
          { key: 'fullname', label: 'Full Name', sortable: false },
          { key: 'platformdepartmentname', label: 'Department', sortable: false },
        ]
      }
      return [
        { key: 'selected', label: '', sortable: false, class: 'text-center', thStyle: { width: '48px' } },
        { key: 'fullname', label: 'Full Name', sortable: false },
        { key: 'workTitle', label: 'Work Title', sortable: false },
      ]
    },
    isClientPaginatedMode() {
      return this.searchType === 'MODE_DEPARTMENT' || this.searchType === 'MODE_POSITION'
    },
    hasActiveSelection() {
      if (this.searchType === 'MODE_GROUP') return !!this.groupSelection
      if (this.searchType === 'MODE_DEPARTMENT') return !!this.departmentSelection
      if (this.searchType === 'MODE_POSITION') return !!this.positionSelection
      return true
    },
    filteredFullList() {
      const q = (this.debouncedSearchQuery || '').toLowerCase()
      if (!q) return this.fullList
      return this.fullList.filter(m => {
        const fullname = (m.fullname || '').toLowerCase()
        const username = (m.username || '').toLowerCase()
        const email = (m.email || '').toLowerCase()
        return fullname.includes(q) || username.includes(q) || email.includes(q)
      })
    },
    tableItems() {
      if (this.isClientPaginatedMode) {
        const start = (this.currentPage - 1) * this.perPage
        return this.filteredFullList.slice(start, start + this.perPage)
      }
      return this.serverItems
    },
    totalUsers() {
      return this.isClientPaginatedMode ? this.filteredFullList.length : this.serverTotal
    },
    dataMeta() {
      const localItemsCount = this.tableItems.length
      const from = this.totalUsers === 0 ? 0 : this.perPage * (this.currentPage - 1) + 1
      return {
        from,
        to: this.totalUsers === 0 ? 0 : Math.min(from + localItemsCount - 1, this.totalUsers),
        of: this.totalUsers,
      }
    },
    emptyText() {
      if (!this.hasActiveSelection) return this.$t('Choose an option above to view employees')
      return this.$t('No matching records found')
    },
    selectedIds() {
      return Object.keys(this.selectedMap)
    },
    selectedCount() {
      return this.selectedIds.length
    },
    pageIds() {
      return this.tableItems.map(item => String(item.id))
    },
    isAllSelectedOnPage() {
      if (!this.pageIds.length) return false
      return this.pageIds.every(id => this.selectedMap[id])
    },
    canSelectAllMatching() {
      return (
        !this.allMatchingSelected &&
        this.isAllSelectedOnPage &&
        this.totalUsers > this.tableItems.length
      )
    },
    selectedSingleMember() {
      if (this.searchType !== 'MODE_SINGLE' || !this.selectedIds.length) return null
      return this.selectedMap[this.selectedIds[0]]
    },
  },
  watch: {
    searchType(newVal, oldVal) {
      this.currentPage = 1
      this.searchQuery = ''
      this.debouncedSearchQuery = ''
      this.serverItems = []
      this.serverTotal = 0
      this.fullList = []
      this.allMatchingSelected = false

      // Single-employee selection is mutually exclusive with the multi-select tabs
      if (newVal === 'MODE_SINGLE' || oldVal === 'MODE_SINGLE') {
        this.clearSelection()
      }

      if (this.searchType === 'MODE_ALL') {
        this.fetchAllEmployees()
      } else if (this.searchType === 'MODE_GROUP' && this.groupSelection) {
        this.fetchGroupMembers()
      } else if (this.searchType === 'MODE_DEPARTMENT' && this.departmentSelection) {
        this.fetchDepartmentMembers()
      } else if (this.searchType === 'MODE_POSITION' && this.positionSelection) {
        this.fetchPositionMembers()
      }
    },
    searchQuery(newValue) {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearchQuery = newValue
        this.currentPage = 1
        this.allMatchingSelected = false
        if (!this.isClientPaginatedMode) this.fetchServerData()
      }, 300)
    },
    currentPage() {
      if (!this.isClientPaginatedMode) this.fetchServerData()
    },
    perPage() {
      this.currentPage = 1
      if (!this.isClientPaginatedMode) this.fetchServerData()
    },
  },
  created() {
    if (!store.hasModule(CLIENT_GROUPS_MODULE)) store.registerModule(CLIENT_GROUPS_MODULE, clientGroupStoreModule)
    if (!store.hasModule(MEMBERS_MODULE)) store.registerModule(MEMBERS_MODULE, memberStoreModule)

    if (this.groupId) {
      store.dispatch(`${CLIENT_GROUPS_MODULE}/fetchGroups`)
        .then(response => {
          const groups = (response.data && response.data.data) || []
          const match = groups.find(g => String(g.id) === String(this.groupId))
          if (match) this.groupName = match.name
        })
        .catch(() => { })
    }
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
  },
  methods: {
    fetchServerData() {
      if (this.searchType === 'MODE_ALL') this.fetchAllEmployees()
      else if (this.searchType === 'MODE_GROUP' && this.groupSelection) this.fetchGroupMembers()
    },
    onSingleMemberChanged(payload) {
      if (payload) {
        this.selectedMap = { [String(payload.id)]: payload }
      } else {
        this.selectedMap = {}
      }
    },
    onGroupChanged(payload) {
      this.groupSelection = payload && payload.length ? payload[0] : null
      this.currentPage = 1
      this.allMatchingSelected = false
      if (this.groupSelection) this.fetchGroupMembers()
      else {
        this.serverItems = []
        this.serverTotal = 0
      }
    },
    onDepartmentChanged(payload) {
      this.departmentSelection = payload || null
      this.currentPage = 1
      this.allMatchingSelected = false
      if (this.departmentSelection) this.fetchDepartmentMembers()
      else this.fullList = []
    },
    onPositionChanged(payload) {
      this.positionSelection = payload || null
      this.currentPage = 1
      this.allMatchingSelected = false
      if (this.positionSelection) this.fetchPositionMembers()
      else this.fullList = []
    },
    fetchGroupMembers() {
      if (!this.groupSelection) return
      this.isLoading = true
      store.dispatch(`${CLIENT_GROUPS_MODULE}/fetchGroupMembers`, {
        id: this.groupSelection.id,
        page: this.currentPage,
        limit: this.perPage,
        search: this.debouncedSearchQuery,
      }).then(response => {
        const { users, total } = response.data
        this.serverItems = users || []
        this.serverTotal = typeof total === 'number' ? total : (users ? users.length : 0)
      }).catch(() => {
        this.notifyError()
      }).finally(() => {
        this.isLoading = false
      })
    },
    fetchAllEmployees() {
      this.isLoading = true
      store.dispatch(`${MEMBERS_MODULE}/fetchAllMembers`, {
        currentPage: this.currentPage,
        perPage: this.perPage,
        searchQuery: this.debouncedSearchQuery,
      }).then(response => {
        const { users, total } = response.data
        this.serverItems = users || []
        this.serverTotal = typeof total === 'number' ? total : (users ? users.length : 0)
      }).catch(() => {
        this.notifyError()
      }).finally(() => {
        this.isLoading = false
      })
    },
    fetchDepartmentMembers() {
      if (!this.departmentSelection) return
      this.isLoading = true
      const deptId = this.departmentSelection.departmentplatformid && this.departmentSelection.departmentplatformid.length > 0
        ? this.departmentSelection.departmentplatformid
        : this.departmentSelection.id
      axios.get('/admin/f/cm/getmembersbydepartment?id=' + deptId)
        .then(response => {
          this.fullList = response.data.data || []
        })
        .catch(() => {
          this.notifyError()
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    fetchPositionMembers() {
      if (!this.positionSelection) return
      this.isLoading = true
      const positionName = this.positionSelection.title || this.positionSelection
      axios.get('/admin/f/cm/getmembersbyposition?name=' + encodeURIComponent(positionName))
        .then(response => {
          this.fullList = response.data.data || []
        })
        .catch(() => {
          this.notifyError()
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    notifyError() {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: this.$t('Error'),
          icon: 'AlertOctagonIcon',
          variant: 'danger',
          text: this.$t('Failed to load all users'),
        },
      })
    },
    isRowSelected(id) {
      return !!this.selectedMap[String(id)]
    },
    toggleRow(item) {
      const id = String(item.id)
      const next = { ...this.selectedMap }
      if (next[id]) delete next[id]
      else next[id] = item
      this.selectedMap = next
      this.allMatchingSelected = false
    },
    toggleSelectAllOnPage() {
      const next = { ...this.selectedMap }
      if (this.isAllSelectedOnPage) {
        this.pageIds.forEach(id => delete next[id])
      } else {
        this.tableItems.forEach(item => { next[String(item.id)] = item })
      }
      this.selectedMap = next
      this.allMatchingSelected = false
    },
    selectAllMatching() {
      if (this.isSelectingAllMatching) return

      const applySelection = (list) => {
        const next = { ...this.selectedMap }
        ;(list || []).forEach(item => { next[String(item.id)] = item })
        this.selectedMap = next
        this.allMatchingSelected = true
      }

      if (this.isClientPaginatedMode) {
        applySelection(this.filteredFullList)
        return
      }

      const limit = this.totalUsers || this.perPage
      this.isSelectingAllMatching = true

      let request
      if (this.searchType === 'MODE_ALL') {
        request = store.dispatch(`${MEMBERS_MODULE}/fetchAllMembers`, {
          currentPage: 1,
          perPage: limit,
          searchQuery: this.debouncedSearchQuery,
        })
      } else if (this.searchType === 'MODE_GROUP' && this.groupSelection) {
        request = store.dispatch(`${CLIENT_GROUPS_MODULE}/fetchGroupMembers`, {
          id: this.groupSelection.id,
          page: 1,
          limit,
          search: this.debouncedSearchQuery,
        })
      } else {
        this.isSelectingAllMatching = false
        return
      }

      request.then(response => {
        applySelection(response.data.users)
      }).catch(() => {
        this.notifyError()
      }).finally(() => {
        this.isSelectingAllMatching = false
      })
    },
    clearSelection() {
      this.selectedMap = {}
      this.allMatchingSelected = false
    },
    handleAddMembers() {
      if (!this.selectedCount || this.submitting) return
      this.submitting = true
      const memberIds = this.selectedIds

      store.dispatch(`${CLIENT_GROUPS_MODULE}/addMembersToGroup`, {
        members: memberIds,
        groupId: [this.groupId],
      }).then(() => {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('Success'),
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        router.push({ name: 'client-group-members', query: { gid: this.groupId } })
      }).catch(error => {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('Error'),
            icon: 'AlertOctagonIcon',
            variant: 'warning',
            text: error && error.message,
          },
        })
      }).finally(() => {
        this.submitting = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.picker-wrap {
  max-width: 480px;
}

.selected-single-preview {
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f8f7ff;
}

.add-participants-footer {
  position: sticky;
  bottom: 0;
  background: white;
  border-top: 1px solid #e8e8e8;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  margin-top: 1rem;
}

.radio-option {
  display: inline-flex;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0;

  input {
    display: none;
  }

  &:hover {
    border-color: #7367f0;
    background: #f8f7ff;
  }

  &.active {
    border-color: #7367f0;
    background: #7367f0;
    color: white;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
